(() => {
  const dashboard = document.querySelector('[data-account-dashboard]');
  if (!dashboard) return;

  const tabs = Array.from(dashboard.querySelectorAll('[data-account-tab]'));
  const panels = Array.from(dashboard.querySelectorAll('[data-account-panel]'));

  dashboard.querySelectorAll('[data-order-item-image]').forEach((image) => {
    const showPlaceholder = () => {
      image.hidden = true;
    };

    image.addEventListener('error', showPlaceholder, {once: true});
    if (image.complete && image.naturalWidth === 0) showPlaceholder();
  });

  const orderList = dashboard.querySelector('[data-orders-list]');
  const orderPagination = dashboard.querySelector('[data-orders-pagination]');

  if (orderList instanceof HTMLElement && orderPagination instanceof HTMLElement) {
    const orderCards = Array.from(orderList.querySelectorAll('[data-order-card]')).filter(
      (card) => card instanceof HTMLElement
    );
    const newerControl = orderPagination.querySelector('[data-orders-newer]');
    const olderControl = orderPagination.querySelector('[data-orders-older]');
    const pageStatus = orderPagination.querySelector('[data-orders-page-status]');
    const mobileOrders = window.matchMedia('(max-width: 620px)');
    const tabletOrders = window.matchMedia('(max-width: 989px)');
    const batchSize = Number.parseInt(orderPagination.dataset.ordersBatchSize || '24', 10) || 24;
    const currentBatch = Number.parseInt(orderPagination.dataset.ordersCurrentBatch || '1', 10) || 1;
    const totalOrders = Number.parseInt(orderPagination.dataset.ordersTotal || String(orderCards.length), 10) || orderCards.length;
    const batchOffset = Math.max(0, (currentBatch - 1) * batchSize);
    const previousBatchUrl = orderPagination.dataset.ordersPreviousUrl || '';
    const nextBatchUrl = orderPagination.dataset.ordersNextUrl || '';
    const sliceParameter = 'hp_orders_slice';
    let ordersPerPage = mobileOrders.matches ? 6 : tabletOrders.matches ? 8 : 12;

    const pagesInBatch = () => Math.max(1, Math.ceil(orderCards.length / ordersPerPage));
    const requestedSlice = () => {
      const requested = new URL(window.location.href).searchParams.get(sliceParameter);
      if (requested === 'last') return pagesInBatch() - 1;
      const parsed = Number.parseInt(requested || '0', 10);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    let localPage = Math.max(0, Math.min(pagesInBatch() - 1, requestedSlice()));

    const updateSliceUrl = (mode = 'replace') => {
      const url = new URL(window.location.href);
      if (localPage > 0) url.searchParams.set(sliceParameter, String(localPage));
      else url.searchParams.delete(sliceParameter);
      const state = {...(window.history.state || {}), hpOrdersSlice: localPage};
      window.history[mode === 'push' ? 'pushState' : 'replaceState'](state, '', url);
    };

    const batchNavigationUrl = (nativeUrl, slice) => {
      const url = new URL(nativeUrl, window.location.href);
      url.searchParams.set('tab', 'orders');
      if (slice === 'last') url.searchParams.set(sliceParameter, 'last');
      else url.searchParams.delete(sliceParameter);
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const renderOrderPage = ({scroll = false, historyMode = 'replace'} = {}) => {
      const batchPageCount = pagesInBatch();
      localPage = Math.max(0, Math.min(batchPageCount - 1, localPage));
      const firstCard = localPage * ordersPerPage;
      const lastCard = firstCard + ordersPerPage;

      orderCards.forEach((card, index) => {
        card.hidden = index < firstCard || index >= lastCard;
      });

      const totalPages = Math.max(1, Math.ceil(totalOrders / ordersPerPage));
      const globalPage = Math.min(totalPages, Math.floor(batchOffset / ordersPerPage) + localPage + 1);
      if (pageStatus instanceof HTMLElement) pageStatus.textContent = `Page ${globalPage} of ${totalPages}`;
      orderPagination.hidden = totalPages <= 1;

      const hasNewer = globalPage > 1;
      const hasOlder = globalPage < totalPages;
      if (newerControl instanceof HTMLElement) newerControl.hidden = !hasNewer;
      if (olderControl instanceof HTMLElement) olderControl.hidden = !hasOlder;

      updateSliceUrl(historyMode);
      if (scroll) orderList.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    const moveOrderPage = (direction) => {
      const nextLocalPage = localPage + direction;
      if (nextLocalPage >= 0 && nextLocalPage < pagesInBatch()) {
        localPage = nextLocalPage;
        renderOrderPage({scroll: true, historyMode: 'push'});
        return;
      }

      const nativeUrl = direction < 0 ? previousBatchUrl : nextBatchUrl;
      if (!nativeUrl) return;
      window.location.assign(batchNavigationUrl(nativeUrl, direction < 0 ? 'last' : 'first'));
    };

    newerControl?.addEventListener('click', (event) => {
      event.preventDefault();
      moveOrderPage(-1);
    });
    olderControl?.addEventListener('click', (event) => {
      event.preventDefault();
      moveOrderPage(1);
    });

    const syncResponsiveOrderPage = () => {
      const firstVisibleOrder = batchOffset + localPage * ordersPerPage;
      const nextOrdersPerPage = mobileOrders.matches ? 6 : tabletOrders.matches ? 8 : 12;
      if (nextOrdersPerPage === ordersPerPage) return;
      ordersPerPage = nextOrdersPerPage;
      localPage = Math.floor(Math.max(0, firstVisibleOrder - batchOffset) / ordersPerPage);
      renderOrderPage();
    };

    mobileOrders.addEventListener('change', syncResponsiveOrderPage);
    tabletOrders.addEventListener('change', syncResponsiveOrderPage);
    window.addEventListener('popstate', () => {
      localPage = Math.max(0, Math.min(pagesInBatch() - 1, requestedSlice()));
      renderOrderPage({historyMode: 'replace'});
    });
    renderOrderPage();
  }

  if (!tabs.length || !panels.length) return;

  const validTabs = new Set(tabs.map((tab) => tab.dataset.accountTab));
  const defaultTab = dashboard.dataset.defaultTab || 'profile';

  const tabFromUrl = () => {
    const requested = new URL(window.location.href).searchParams.get('tab');
    return validTabs.has(requested) ? requested : defaultTab;
  };

  const activate = (name, updateHistory = false) => {
    if (!validTabs.has(name)) name = defaultTab;

    tabs.forEach((tab) => {
      const active = tab.dataset.accountTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel) => {
      const active = panel.dataset.accountPanel === name;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', name);
      window.history.pushState({accountTab: name}, '', url);
    }
  };

  tabs.forEach((tab) => {
    tab.id = `hp-account-tab-${tab.dataset.accountTab}`;
    tab.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      activate(tab.dataset.accountTab, true);
      dashboard.querySelector(`[data-account-panel="${tab.dataset.accountTab}"]`)?.focus({preventScroll: true});
    });

    tab.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextTab = tabs[(currentIndex + direction + tabs.length) % tabs.length];
      nextTab.focus();
      nextTab.click();
    });
  });

  panels.forEach((panel) => {
    panel.tabIndex = -1;
  });

  window.addEventListener('popstate', () => activate(tabFromUrl()));
  activate(tabFromUrl());
})();
